import {
	createGuid,
	FormItemTypes,
	IDictionary,
	isNullOrUndefined,
	MLHelper,
	lazyRetry
} from '@bimser/core';
import { JSX } from 'react';
import {
	DataSourceType,
	IDataSource,
	IPropertyField,
	IContextMenuExecuteActionButton
} from '../../../../entities/common';
import { getDataSourceData, getResultData } from '../../../../helper';
import VisualControl from '../../base/VisualControl';
import { IFormLoadEvent } from '../Form/entities';
import { IContextMenuProperties } from './entities/IContextMenuProperties';
import { Liquid } from 'liquidjs';
const liquidEngine = new Liquid();

export default class ContextMenu extends VisualControl {
	items: Array<IContextMenuExecuteActionButton>;
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	itemsExpr?: string;
	parentKey?: string;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.ContextMenu;
		this.items = [];
		this.dataSourceType = 'dynamic';
		this.itemsExpr = '';
		this.parentKey = '';
		this.dataSource = {
			name: '',
			valueExpr: '',
			displayExpr: [],
			displayFormat: '',
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.caption.visible = false;
	}

	__loadProperties(data: IContextMenuProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.dataSourceType = data.dataSourceType;
		this.__proxyChanged.itemsExpr = data.itemsExpr;
		this.__proxyChanged.parentKey = data.parentKey;
		this.__proxyChanged.items = data.items;
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();

		return [
			...fields,
			{
				name: 'items',
				type: 'array'
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			itemsExpr: this.itemsExpr,
			parentKey: this.parentKey,
			items: this.items,
			dataSource: this.dataSource,
			dataSourceType: this.dataSourceType
		};
	}
	__getContextItemKey(item: any): string {
		return item[this.dataSource.valueExpr] || item.name || createGuid();
	}
	__getContextItemText(item: any): string {
		return MLHelper.getMLText(item.key) || 'No have text value';
	}

	__getContextItemChildren(item: any, allItem: Array<any>): Array<any> {
		if (this.parentKey) {
			const rootKey = this.__getContextItemKey(item);
			return allItem?.filter(x => x[this.parentKey] === rootKey);
		} else if (this.itemsExpr) {
			return item[this.itemsExpr] || item.children || [];
		}
		return [];
	}

	__getFilteredData(data: Array<any>) {
		return this.parentKey
			? data.filter(x => !data.find(i => this.__getContextItemKey(i) == x[this.parentKey]))
			: data;
	}

	__getConvertedItems(
		currentItems: Array<IContextMenuExecuteActionButton>,
		allData: Array<any>,
		getTextByDisplayExpression?: (item?: any) => string
	) {
		const items: Array<IContextMenuExecuteActionButton> = new Array();

		currentItems.forEach(item => {
			const text = getTextByDisplayExpression
				? getTextByDisplayExpression(item)
				: this.__getContextItemText(item);
			const mlObject = MLHelper.getMLObjectWithText(text);
			const name = this.__getContextItemKey(item);
			const children = this.__getConvertedItems(
				this.__getContextItemChildren(item, allData),
				allData,
				getTextByDisplayExpression
			);

			items.push({
				name: name,
				text: mlObject,
				title: mlObject,
				icon: item.icon,
				typeName: item.typeName || ('ContextItemArgs' as any),
				visible: item.visible ?? true,
				args: { children }
			});
		});
		return items;
	}

	__liquidRenderer() {
		return item => {
			let displayFormat = this.dataSource.displayFormat;

			if (displayFormat.includes('.__userCulture')) {
				displayFormat = displayFormat.replace(
					'.__userCulture',
					`['${this.__form.currentLanguage.culture}']`
				);
			}

			return liquidEngine.parseAndRenderSync(displayFormat, item);
		};
	}

	__onLoad(args: IFormLoadEvent, externalData?: any, reload?: boolean) {
		super.__onLoad(args);

		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.__form.serviceUrl &&
			this.__form.displayMode !== 'preview' &&
			this.__form.projectName &&
			this.dataSourceType === 'dynamic' &&
			!this.dataSource.runAtServer
		) {
			this.__proxyChanging.loading = true;

			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				projectVersion: this.__form.packageVersion
			}).then(response => {
				const data = getResultData(response.data);
				if (data?.length) {
					const tId = this.__form.beginTransaction();

					let awaitArgs: Promise<(item?: any) => string> = null;

					if (this.dataSource.displayExpr && this.dataSource.displayFormat) {
						awaitArgs = new Promise(resolve => {
							resolve(this.__liquidRenderer());
						});
					}
					Promise.all([awaitArgs]).then(converterArgs => {
						this.__proxyChanging.items = this.__getConvertedItems(
							this.__getFilteredData(data),
							data,
							converterArgs[0]
						);
						this.__form.endTransaction(tId).then(() => {
							this.__proxyChanging.loading = false;
						});
					});
				} else {
					this.__proxyChanging.items = [];
					this.__proxyChanging.loading = false;
				}
			});
		}
	}

	reload() {
		if (this.dataSource && !isNullOrUndefined(this.dataSource.name)) {
			if (this.dataSource.parameters && this.dataSource.parameters.length) {
				this.__reloadDataSource(this.dataSource, true);
			} else {
				this.__onLoad({}, null, true);
			}
		}
	}

	__render(children?: JSX.Element) {
		return super.__render(<></>);
	}
}
